import { Link } from "@inertiajs/react";
import { RotateCcw } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Button } from "@/components/ui/button";

function RotateRow() {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">Student</th>
            <th className="text-left px-4 py-3 font-semibold">Activation code</th>
            <th className="text-right px-4 py-3 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          <tr>
            <td className="px-4 py-3">
              <div className="font-medium text-foreground">Sarah Thompson</div>
              <div className="text-xs text-muted-foreground">Activated</div>
            </td>
            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">BEAR-MOON-4217</td>
            <td className="px-4 py-3 text-right">
              <Button variant="ghost" size="sm" aria-label="New activation code for Sarah">
                <RotateCcw className="h-4 w-4" />
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function RotatingActivationCodes() {
  return (
    <>
      <p>
        Every student has an <strong>activation code</strong> on their take-home sheet — a friendly word combo like <strong>BEAR-MOON-4217</strong>. It's how parents sign up to follow their child. If a sheet gets lost, lands in the wrong backpack, or shows up somewhere it shouldn't, you can swap the code for a new one. The old one stops working immediately.
      </p>

      <p>
        School admins can do this for any student, from the classroom page or the student's own page. Teachers and parents can't; they'll need to ask a school admin.
      </p>

      <h2>How to do it</h2>
      <Steps>
        <Step title="Find the student">
          <p>Open the classroom from your sidebar, then find the student in the list.</p>
        </Step>
        <Step title="Click the new-code button">
          <p>It's the circle-arrow at the end of the student's row (hover over it and it says <strong>New activation code</strong>). We'll ask you to confirm before going ahead.</p>
          <Screenshot caption="Where to find the swap-code button">
            <RotateRow />
          </Screenshot>
        </Step>
        <Step title="Reprint just that student's sheet">
          <p>
            On the same row, click the download icon to get just that student's sheet.
          </p>
        </Step>
        <Step title="Send the new sheet home">
          <p>
            Same way as the first time — backpack, parent pickup, however you got the original out.
          </p>
        </Step>
      </Steps>

      <Callout variant="note" title="Already-activated parents stay activated">
        <p>
          If a parent already used the old code to sign up, they keep their access — the swap only stops <em>new</em> sign-ups with the old code. So if you're swapping just because a sheet went somewhere it shouldn't have, you won't kick off anyone who's already in.
        </p>
        <p>
          If someone who shouldn't have access already signed up, a school admin can remove them: open the student, find <strong>Families</strong>, and click <strong>Remove</strong> next to their email.
        </p>
      </Callout>

      <h2>When you'd swap a code</h2>
      <ul>
        <li>A take-home sheet got lost on the way home.</li>
        <li>A sheet ended up with the wrong family.</li>
        <li>A parent thinks their code might have been shared somewhere it shouldn't.</li>
        <li>A student moved schools mid-campaign and you want to retire their code.</li>
      </ul>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/printing-take-home-sheets">Printing take-home sheets</Link>.</li>
        <li><Link href="/help/articles/activating-your-child">What parents see when they sign up</Link> — handy when you're walking a parent through it on the phone.</li>
      </ul>
    </>
  );
}
